// Device chains, heard — approximately.
//
// Live's devices don't travel: the wire carries a chain's device NAMES and
// its parameters (name, value, range). What the browser can do with that is
// stand in an equivalent for the devices it recognises, driven by the Set's
// own parameter values, so a producer sweeping a knob in the browser hears
// the sweep move the way it will in Live:
//
// - EQ Eight: its eight bands as the biquads eq-response.ts computes (the
//   curve the editor draws is the curve played), cuts at 12 or 48 dB/oct
// - Delay / Echo / Filter Delay / Grain Delay: a stereo delay, synced to the
//   tempo when the Set's side is, ping-pong, its band filter in the loop
// - Reverb / Hybrid Reverb: pre-delay, input filter, a synthetic room whose
//   size, decay, damping and stereo image follow the Set's values
// - Auto Filter, EQ Three, Channel EQ, compressors, the limiter, saturation,
//   Redux, Chorus-Ensemble, Phaser-Flanger, Auto Pan, Utility
//
// It is not Live's sound and never claims to be: anything unrecognised
// passes through, and the whole thing is one preference away from dry.
//
// Two halves: `stagesFor` is pure (a chain → stage descriptors, unit-tested);
// `buildGraph` turns stages into WebAudio nodes on any BaseAudioContext, so
// the live bus and the offline mixdown share one interpretation.

import { isPlaceholder } from "@shared/sync/devices.js";
import type { DeviceState } from "@shared/sync/state.js";
import { stockDevice } from "@web-shared/lib/live-device-defaults";
import {
  clamp,
  delayFilterBand,
  delayModel,
  deviceOn,
  divisionBeats,
  eqEightBands,
  findParam,
  itemOf,
  normOf,
  readDb,
  readHz,
  readOn,
  reverbModel,
} from "@web-shared/lib/device-params";
import { bandFilters, type BiquadSpec } from "@web-shared/lib/eq-response";

export type Stage =
  | { kind: "filter"; type: BiquadFilterType; frequency: number; q: number }
  | { kind: "eq3"; low: number; mid: number; high: number; lowFreq: number; highFreq: number }
  | { kind: "eq8"; filters: BiquadSpec[]; output: number }
  | { kind: "compressor"; threshold: number; ratio: number; attack: number; release: number; knee: number; makeup: number }
  | { kind: "shaper"; drive: number; mix: number }
  | { kind: "crush"; bits: number; mix: number }
  | {
      kind: "reverb";
      decaySec: number;
      mix: number;
      predelaySec: number;
      /** 0..1 — early reflections spread and density. */
      size: number;
      /** 0..1 stereo image. */
      width: number;
      /** 0..1 high-frequency loss along the tail, above `dampFreq`. */
      damping: number;
      dampFreq: number;
      lowCut: number | null;
      highCut: number | null;
      /** Linear levels of the early reflections and the diffuse tail. */
      reflect: number;
      diffuse: number;
    }
  | { kind: "delay"; left: number; right: number; feedback: number; mix: number; pingPong: boolean; freeze: boolean; filter: { low: number; high: number } | null }
  | { kind: "chorus"; rateHz: number; baseSec: number; depthSec: number; feedback: number; mix: number }
  | { kind: "phaser"; rateHz: number; centerHz: number; depth: number; feedback: number; mix: number }
  | { kind: "autopan"; rateHz: number; amount: number; shape: OscillatorType; tremolo: boolean }
  | { kind: "utility"; gain: number; pan: number; width: number; invertL: boolean; invertR: boolean };

/** The stage kinds this model knows, by device name — what the device view
 * marks as heard. */
export const HEARD_DEVICES: ReadonlySet<string> = new Set([
  "Auto Filter",
  "Auto Pan",
  "Auto Pan-Tremolo",
  "Channel EQ",
  "Chorus-Ensemble",
  "Compressor",
  "Delay",
  "Drum Buss",
  "Echo",
  "EQ Eight",
  "EQ Three",
  "Erosion",
  "Filter Delay",
  "Glue Compressor",
  "Grain Delay",
  "Hybrid Reverb",
  "Limiter",
  "Overdrive",
  "Pedal",
  "Phaser-Flanger",
  "Redux",
  "Reverb",
  "Roar",
  "Saturator",
  "Utility",
  "Vinyl Distortion",
]);

const p = (d: DeviceState, ...names: string[]) => findParam(d, ...names)?.p;

/** A time in seconds (ms or s told apart by the range; a quantized one is an
 * index into names this model doesn't know: the fallback). */
function seconds(d: DeviceState, fallback: number, ...names: string[]): number {
  const t = p(d, ...names);
  if (!t || t.quantized) return fallback;
  return clamp(t.max > 30 ? t.value / 1000 : t.value, 0, 60);
}

/** A rate in Hz, or the synced division at `bpm` when the device syncs. */
function rate(d: DeviceState, bpm: number, fallbackHz: number): number {
  const sync = p(d, "Sync", "LFO Sync", "Rate Type");
  const synced = sync && (itemOf(sync)?.toLowerCase().includes("sync") ?? readOn(sync, false)) && !itemOf(sync)?.toLowerCase().includes("free");
  if (synced) {
    const beats = divisionBeats(itemOf(p(d, "Synced Rate", "Sync Rate", "LFO Sync Rate", "Rate (Sync)")), false);
    if (beats && beats > 0) return clamp(bpm / 60 / beats, 0.01, 40);
  }
  const hz = p(d, "Frequency", "Rate", "LFO Frequency", "LFO Rate", "Rate (Hz)");
  if (!hz) return fallbackHz;
  if (hz.min >= 0 && hz.max <= 1.0001) return clamp(0.01 * Math.pow(4000, normOf(hz, 0.3)), 0.01, 40);
  return clamp(hz.value, 0.01, 40);
}

const FILTER_TYPES: Record<string, BiquadFilterType> = {
  lowpass: "lowpass",
  highpass: "highpass",
  bandpass: "bandpass",
  notch: "notch",
  morph: "bandpass",
  "low pass": "lowpass",
  "high pass": "highpass",
  "band pass": "bandpass",
};

/** The stages a chain reduces to, in order; unrecognised devices contribute
 * none. `bpm` places tempo-synced times (Delay's divisions, an LFO's rate). */
export function stagesFor(devices: readonly DeviceState[] | undefined, bpm = 120): Stage[] {
  const out: Stage[] = [];
  for (const d of devices ?? []) {
    // A browser's insert a Set has yet to fill in: heard at Live's stock values.
    const dev = isPlaceholder(d) ? (stockDevice(d.name) ?? d) : d;
    if (!deviceOn(dev)) continue;
    const stage = stageFor(dev, bpm);
    if (stage) out.push(stage);
  }
  return out;
}

/** One device's stage, whether or not it's switched on (what its view draws). */
export function deviceStage(d: DeviceState, bpm = 120): Stage | null {
  return stageFor(d, bpm);
}

function stageFor(d: DeviceState, bpm: number): Stage | null {
  switch (d.name) {
    case "Auto Filter": {
      const type = p(d, "Filter Type", "Type");
      const item = itemOf(type)?.toLowerCase() ?? "lowpass";
      return {
        kind: "filter",
        type: FILTER_TYPES[item] ?? "lowpass",
        frequency: clamp(readHz(p(d, "Frequency", "Filter Freq", "Cutoff"), 20, 20000, 20000), 20, 20000),
        q: 0.5 + normOf(p(d, "Resonance", "Res"), 0.2) * 12,
      };
    }
    case "EQ Eight": {
      const filters = eqEightBands(d).flatMap((b) => bandFilters(b));
      const scale = p(d, "Scale");
      const k = scale ? clamp(scale.value, -2, 2) : 1;
      return {
        kind: "eq8",
        filters: k === 1 ? filters : filters.map((f) => ({ ...f, gain: f.gain * k })),
        output: clamp(readDb(p(d, "Output Gain", "Gain"), 0), -24, 24),
      };
    }
    case "EQ Three": {
      const kill = (name: string): boolean => !readOn(p(d, name), true);
      const fLo = p(d, "FreqLo", "Low Freq");
      const fHi = p(d, "FreqHi", "High Freq");
      return {
        kind: "eq3",
        low: kill("LowOn") ? -96 : readDb(p(d, "GainLo", "Low Gain"), 0),
        mid: kill("MidOn") ? -96 : readDb(p(d, "GainMid", "Mid Gain"), 0),
        high: kill("HighOn") ? -96 : readDb(p(d, "GainHi", "High Gain"), 0),
        lowFreq: fLo ? clamp(readHz(fLo, 50, 5000, 250), 50, 5000) : 250,
        highFreq: fHi ? clamp(readHz(fHi, 500, 15000, 2500), 500, 15000) : 2500,
      };
    }
    case "Channel EQ":
      return {
        kind: "eq3",
        low: readDb(p(d, "Low Gain"), 0),
        mid: readDb(p(d, "Mid Gain"), 0),
        high: readDb(p(d, "High Gain"), 0),
        lowFreq: 200,
        highFreq: clamp(readHz(p(d, "Mid Freq"), 120, 7500, 4000) * 2, 500, 15000),
      };
    case "Compressor":
    case "Glue Compressor": {
      const ratio = p(d, "Ratio");
      return {
        kind: "compressor",
        threshold: clamp(readDb(p(d, "Threshold"), -18), -60, 0),
        ratio: ratio ? clamp(ratio.value, 1, 20) : 4,
        attack: seconds(d, 0.01, "Attack"),
        release: seconds(d, 0.1, "Release"),
        knee: clamp(p(d, "Knee")?.value ?? 6, 0, 40),
        makeup: clamp(readDb(p(d, "Output Gain", "Makeup", "Output"), 0), -24, 24),
      };
    }
    case "Limiter":
      return {
        kind: "compressor",
        threshold: clamp(readDb(p(d, "Ceiling"), -0.3), -60, 0),
        ratio: 20,
        attack: 0.001,
        release: seconds(d, 0.05, "Release"),
        knee: 0,
        makeup: clamp(readDb(p(d, "Gain"), 0), -24, 24),
      };
    case "Saturator":
    case "Overdrive":
    case "Pedal":
    case "Roar":
    case "Vinyl Distortion":
    case "Erosion":
    case "Drum Buss":
      return { kind: "shaper", drive: normOf(p(d, "Drive", "Amount"), 0.2), mix: normOf(p(d, "Dry/Wet", "Dry Wet", "Mix"), 1) };
    case "Redux": {
      const bitOn = p(d, "Bit On");
      const bits = p(d, "Bit Depth", "Bit Reduction", "Bits");
      return {
        kind: "crush",
        bits: bitOn && !readOn(bitOn, true) ? 24 : bits ? clamp(Math.round(bits.value), 1, 24) : 8,
        mix: normOf(p(d, "Dry/Wet", "Dry Wet", "Mix"), 1),
      };
    }
    case "Reverb":
    case "Hybrid Reverb": {
      const r = reverbModel(d);
      return {
        kind: "reverb",
        decaySec: r.freeze ? 12 : clamp(r.decayMs / 1000, 0.1, 12),
        mix: r.mix,
        predelaySec: r.predelayMs / 1000,
        size: r.size,
        width: r.width,
        damping: r.damping,
        dampFreq: r.dampFreq,
        lowCut: r.lowCut,
        highCut: r.highCut,
        reflect: Math.pow(10, r.reflectDb / 20),
        diffuse: Math.pow(10, r.diffuseDb / 20),
      };
    }
    case "Delay":
    case "Echo":
    case "Filter Delay":
    case "Grain Delay": {
      const m = delayModel(d, bpm);
      return {
        kind: "delay",
        left: m.left.timeSec,
        right: m.right.timeSec,
        feedback: m.feedback,
        mix: m.mix,
        pingPong: m.pingPong,
        freeze: m.freeze,
        filter: m.filterOn ? delayFilterBand(m.filterFreq, m.filterWidth) : null,
      };
    }
    case "Chorus-Ensemble":
      return {
        kind: "chorus",
        rateHz: rate(d, bpm, 0.8),
        baseSec: 0.012,
        depthSec: 0.001 + normOf(p(d, "Amount", "Depth"), 0.4) * 0.006,
        feedback: clamp(normOf(p(d, "Feedback"), 0) * 0.9, 0, 0.9),
        mix: normOf(p(d, "Dry/Wet", "Dry Wet", "Mix"), 0.5),
      };
    case "Phaser-Flanger": {
      const mode = itemOf(p(d, "Mode"))?.toLowerCase() ?? "phaser";
      const mix = normOf(p(d, "Dry/Wet", "Dry Wet", "Mix"), 0.5);
      const fb = clamp(normOf(p(d, "Feedback"), 0.3) * 0.9, 0, 0.9);
      if (mode.includes("flang") || mode.includes("doubl")) {
        const doubler = mode.includes("doubl");
        return { kind: "chorus", rateHz: rate(d, bpm, 0.5), baseSec: doubler ? 0.02 : 0.003, depthSec: 0.0005 + normOf(p(d, "Amount", "Depth"), 0.5) * (doubler ? 0.004 : 0.0025), feedback: doubler ? 0 : fb, mix };
      }
      return { kind: "phaser", rateHz: rate(d, bpm, 0.5), centerHz: readHz(p(d, "Center Freq", "Frequency", "Center"), 50, 18000, 800), depth: normOf(p(d, "Amount", "Depth"), 0.5), feedback: fb, mix };
    }
    case "Auto Pan":
    case "Auto Pan-Tremolo": {
      const shape = itemOf(p(d, "Waveform", "Shape", "LFO Waveform"))?.toLowerCase() ?? "sine";
      const osc: OscillatorType = shape.includes("tri") ? "triangle" : shape.includes("saw") ? "sawtooth" : shape.includes("square") || shape.includes("pulse") ? "square" : "sine";
      const mode = itemOf(p(d, "Mode"))?.toLowerCase() ?? "";
      return { kind: "autopan", rateHz: rate(d, bpm, 1), amount: normOf(p(d, "Amount"), 0.5), shape: osc, tremolo: mode.includes("trem") };
    }
    case "Utility": {
      if (readOn(p(d, "Mute"), false)) return { kind: "utility", gain: 0, pan: 0, width: 1, invertL: false, invertR: false };
      const pan = p(d, "Balance", "Panorama", "Pan");
      const width = p(d, "Stereo Width", "Width");
      return {
        kind: "utility",
        gain: Math.pow(10, readDb(p(d, "Gain"), 0) / 20),
        pan: pan ? (pan.min < 0 ? clamp(pan.value / Math.max(Math.abs(pan.min), pan.max), -1, 1) : normOf(pan, 0.5) * 2 - 1) : 0,
        width: readOn(p(d, "Mono"), false) ? 0 : width ? clamp(width.value, 0, 4) : 1,
        invertL: readOn(p(d, "Left Inv", "Phz-L", "Left Phase"), false),
        invertR: readOn(p(d, "Right Inv", "Phz-R", "Right Phase"), false),
      };
    }
    default:
      return null;
  }
}

export interface DeviceGraph {
  input: AudioNode;
  output: AudioNode;
  /** Move the parameters to `stages` in place when the stage KINDS (and
   * topologies) line up — a knob turned, a mix moved — so reverb and delay
   * tails ride through and compressors keep their state. False when the
   * shape differs, and the caller rebuilds. */
  update(stages: readonly Stage[]): boolean;
  dispose(): void;
}

// ---- reverb impulses --------------------------------------------------------

/** Impulses by their shaping values — a 12 s room is a million samples, not
 * something to synthesise again for every knob on the same lane. */
const impulses = new Map<string, AudioBuffer>();
const IMPULSE_CACHE_MAX = 12;

type RoomShape = Pick<Extract<Stage, { kind: "reverb" }>, "decaySec" | "size" | "width" | "damping" | "dampFreq" | "reflect" | "diffuse">;

function impulseKey(rate: number, r: RoomShape): string {
  const q = (v: number, k: number) => Math.round(v * k);
  return [rate, q(r.decaySec, 100), q(r.size, 50), q(r.width, 20), q(r.damping, 20), q(r.dampFreq, 0.01), q(r.reflect, 20), q(r.diffuse, 20)].join(":");
}

function impulse(ctx: BaseAudioContext, r: RoomShape): AudioBuffer {
  const key = impulseKey(ctx.sampleRate, r);
  const cached = impulses.get(key);
  if (cached) return cached;
  const built = synthesiseImpulse(ctx, r);
  if (impulses.size >= IMPULSE_CACHE_MAX) impulses.delete(impulses.keys().next().value!);
  impulses.set(key, built);
  return built;
}

/** A synthetic room: a handful of early reflections spread over a window
 * the room's size sets, then exponentially decaying noise (to -60 dB at the
 * decay time) whose highs fall away faster with damping, and a stereo image
 * from fully correlated (0) to independent channels (1). */
export function synthesiseImpulse(ctx: BaseAudioContext, r: RoomShape): AudioBuffer {
  const rate = ctx.sampleRate;
  const frames = Math.max(1, Math.round(r.decaySec * rate));
  const buf = ctx.createBuffer(2, frames, rate);
  const raw: Float32Array[] = [0, 1].map(() => new Float32Array(frames));
  const erWindow = (0.005 + r.size * 0.08) * rate;
  const onset = Math.min(frames - 1, Math.round(erWindow * 0.6));
  for (let ch = 0; ch < 2; ch++) {
    const data = raw[ch]!;
    let seed = 0x9e3779b9 + ch * 7919;
    const rnd = (): number => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 0xffffffff;
    };
    // Tail: -60 dB at the decay time.
    const k = Math.log(1000) / frames;
    let lp = 0;
    const baseCut = Math.min(0.99, (2 * Math.PI * r.dampFreq) / rate);
    for (let i = 0; i < frames; i++) {
      const white = rnd() * 2 - 1;
      const t = i / frames;
      // The one-pole's cutoff closes along the tail when damped.
      const a = Math.max(0.01, 1 - t * r.damping * (1 - baseCut));
      lp += a * (white - lp);
      const env = Math.exp(-k * i) * (i < onset ? i / Math.max(1, onset) : 1);
      data[i] = lp * env * r.diffuse * 0.6;
    }
    // Early reflections.
    const taps = 6 + Math.round(r.size * 10);
    for (let j = 0; j < taps; j++) {
      const at = Math.min(frames - 1, Math.round(rnd() * erWindow));
      data[at]! += (rnd() < 0.5 ? -1 : 1) * r.reflect * (0.8 - (0.5 * at) / Math.max(1, erWindow));
    }
  }
  // Stereo image: blend towards the mid when narrow.
  const w = clamp(r.width, 0, 1);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);
  for (let i = 0; i < frames; i++) {
    const l = raw[0]![i]!;
    const rr = raw[1]![i]!;
    const mid = (l + rr) / 2;
    L[i] = mid + (l - mid) * w;
    R[i] = mid + (rr - mid) * w;
  }
  return buf;
}

function shaperCurve(drive: number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const curve = new Float32Array(new ArrayBuffer(n * 4));
  const k = 1 + drive * 40;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * k) / Math.tanh(k);
  }
  return curve;
}

/** A staircase at `bits` of resolution. */
function crushCurve(bits: number): Float32Array<ArrayBuffer> {
  const n = 4096;
  const curve = new Float32Array(new ArrayBuffer(n * 4));
  const steps = Math.pow(2, clamp(bits, 1, 24) - 1);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.round(x * steps) / steps;
  }
  return curve;
}

// ---- building ---------------------------------------------------------------

/** One built stage: its ends, its nodes, and how to move it in place. */
interface Built {
  input: AudioNode;
  output: AudioNode;
  nodes: AudioNode[];
  sources: AudioScheduledSourceNode[];
  /** Same kind and a topology this build can reach by setting values. */
  accepts(s: Stage): boolean;
  apply(s: Stage): void;
}

/** A dry/wet split around a wet path. */
function mixer(ctx: BaseAudioContext): { input: GainNode; output: GainNode; dry: GainNode; wet: GainNode; set(mix: number): void } {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  input.connect(dry);
  dry.connect(output);
  wet.connect(output);
  return {
    input,
    output,
    dry,
    wet,
    set: (mix) => {
      dry.gain.value = 1 - mix;
      wet.gain.value = mix;
    },
  };
}

/** A node that upmixes whatever arrives to stereo the way speakers do —
 * a splitter alone reads a mono source as left-only. */
function stereo(ctx: BaseAudioContext): GainNode {
  const g = ctx.createGain();
  g.channelCount = 2;
  g.channelCountMode = "explicit";
  g.channelInterpretation = "speakers";
  return g;
}

function setBiquad(node: BiquadFilterNode, s: BiquadSpec): void {
  node.type = s.type;
  node.frequency.value = clamp(s.frequency, 10, 22000);
  node.Q.value = s.Q;
  node.gain.value = s.gain;
}

/** An LFO: an oscillator into a depth gain; the caller connects `out`. */
function lfo(ctx: BaseAudioContext, type: OscillatorType = "sine"): { osc: OscillatorNode; out: GainNode } {
  const osc = ctx.createOscillator();
  osc.type = type;
  const out = ctx.createGain();
  osc.connect(out);
  osc.start(0);
  return { osc, out };
}

function buildStage(ctx: BaseAudioContext, s: Stage): Built {
  switch (s.kind) {
    case "filter": {
      const f = ctx.createBiquadFilter();
      return {
        input: f,
        output: f,
        nodes: [f],
        sources: [],
        accepts: (n) => n.kind === "filter",
        apply: (n) => {
          if (n.kind !== "filter") return;
          f.type = n.type;
          f.frequency.value = n.frequency;
          f.Q.value = n.q;
        },
      };
    }
    case "eq3": {
      const lo = ctx.createBiquadFilter();
      lo.type = "lowshelf";
      const mid = ctx.createBiquadFilter();
      mid.type = "peaking";
      mid.Q.value = 0.7;
      const hi = ctx.createBiquadFilter();
      hi.type = "highshelf";
      lo.connect(mid);
      mid.connect(hi);
      return {
        input: lo,
        output: hi,
        nodes: [lo, mid, hi],
        sources: [],
        accepts: (n) => n.kind === "eq3",
        apply: (n) => {
          if (n.kind !== "eq3") return;
          lo.frequency.value = n.lowFreq;
          lo.gain.value = n.low;
          mid.frequency.value = Math.sqrt(n.lowFreq * n.highFreq);
          mid.gain.value = n.mid;
          hi.frequency.value = n.highFreq;
          hi.gain.value = n.high;
        },
      };
    }
    case "eq8": {
      const input = ctx.createGain();
      const out = ctx.createGain();
      const filters = s.filters.map(() => ctx.createBiquadFilter());
      let tail: AudioNode = input;
      for (const f of filters) {
        tail.connect(f);
        tail = f;
      }
      tail.connect(out);
      return {
        input,
        output: out,
        nodes: [input, ...filters, out],
        sources: [],
        accepts: (n) => n.kind === "eq8" && n.filters.length === filters.length,
        apply: (n) => {
          if (n.kind !== "eq8") return;
          n.filters.forEach((spec, i) => setBiquad(filters[i]!, spec));
          out.gain.value = Math.pow(10, n.output / 20);
        },
      };
    }
    case "compressor": {
      const c = ctx.createDynamicsCompressor();
      const makeup = ctx.createGain();
      c.connect(makeup);
      return {
        input: c,
        output: makeup,
        nodes: [c, makeup],
        sources: [],
        accepts: (n) => n.kind === "compressor",
        apply: (n) => {
          if (n.kind !== "compressor") return;
          c.threshold.value = n.threshold;
          c.ratio.value = n.ratio;
          c.attack.value = Math.min(1, n.attack);
          c.release.value = Math.min(1, n.release);
          c.knee.value = n.knee;
          makeup.gain.value = Math.pow(10, n.makeup / 20);
        },
      };
    }
    case "shaper":
    case "crush": {
      const m = mixer(ctx);
      const w = ctx.createWaveShaper();
      if (s.kind === "shaper") w.oversample = "2x";
      const trim = ctx.createGain();
      m.input.connect(w);
      w.connect(trim);
      trim.connect(m.wet);
      let shaped = -1;
      const kind = s.kind;
      return {
        input: m.input,
        output: m.output,
        nodes: [m.input, m.output, m.dry, m.wet, w, trim],
        sources: [],
        accepts: (n) => n.kind === kind,
        apply: (n) => {
          if (n.kind === "shaper") {
            if (shaped !== n.drive) {
              w.curve = shaperCurve(n.drive);
              shaped = n.drive;
            }
            trim.gain.value = 1 / (1 + n.drive * 2);
            m.set(n.mix);
          } else if (n.kind === "crush") {
            if (shaped !== n.bits) {
              w.curve = crushCurve(n.bits);
              shaped = n.bits;
            }
            m.set(n.mix);
          }
        },
      };
    }
    case "reverb": {
      const m = mixer(ctx);
      const pre = ctx.createDelay(1);
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      const conv = ctx.createConvolver();
      m.input.connect(pre);
      pre.connect(hp);
      hp.connect(lp);
      lp.connect(conv);
      conv.connect(m.wet);
      let key = "";
      return {
        input: m.input,
        output: m.output,
        nodes: [m.input, m.output, m.dry, m.wet, pre, hp, lp, conv],
        sources: [],
        accepts: (n) => n.kind === "reverb",
        apply: (n) => {
          if (n.kind !== "reverb") return;
          const k = impulseKey(ctx.sampleRate, n);
          if (k !== key) {
            conv.buffer = impulse(ctx, n);
            key = k;
          }
          pre.delayTime.value = clamp(n.predelaySec, 0, 1);
          hp.frequency.value = n.lowCut ?? 10;
          lp.frequency.value = n.highCut ?? 22000;
          m.set(n.mix);
        },
      };
    }
    case "delay": {
      // Two lines, each fed through the band filter. Straight: L→L, R→R.
      // Ping-pong: the input summed into the left line, which feeds the
      // right, which feeds the left. All of it gains, so a toggle moves in
      // place and the echoes keep ringing.
      const m = mixer(ctx);
      const split = ctx.createChannelSplitter(2);
      const merge = ctx.createChannelMerger(2);
      const inLL = ctx.createGain();
      const inRL = ctx.createGain();
      const inRR = ctx.createGain();
      const mk = () => {
        const hp = ctx.createBiquadFilter();
        hp.type = "highpass";
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        const d = ctx.createDelay(5);
        hp.connect(lp);
        lp.connect(d);
        return { hp, lp, d };
      };
      const L = mk();
      const R = mk();
      const fbLL = ctx.createGain();
      const fbRR = ctx.createGain();
      const fbLR = ctx.createGain();
      const fbRL = ctx.createGain();
      const up = stereo(ctx);
      m.input.connect(up);
      up.connect(split);
      split.connect(inLL, 0);
      split.connect(inRL, 1);
      split.connect(inRR, 1);
      inLL.connect(L.hp);
      inRL.connect(L.hp);
      inRR.connect(R.hp);
      L.d.connect(fbLL);
      fbLL.connect(L.hp);
      R.d.connect(fbRR);
      fbRR.connect(R.hp);
      L.d.connect(fbLR);
      fbLR.connect(R.hp);
      R.d.connect(fbRL);
      fbRL.connect(L.hp);
      L.d.connect(merge, 0, 0);
      R.d.connect(merge, 0, 1);
      merge.connect(m.wet);
      return {
        input: m.input,
        output: m.output,
        nodes: [m.input, m.output, m.dry, m.wet, up, split, merge, inLL, inRL, inRR, L.hp, L.lp, L.d, R.hp, R.lp, R.d, fbLL, fbRR, fbLR, fbRL],
        sources: [],
        accepts: (n) => n.kind === "delay",
        apply: (n) => {
          if (n.kind !== "delay") return;
          // A delay inside a loop can't be shorter than one render quantum.
          const min = 128 / ctx.sampleRate;
          L.d.delayTime.value = clamp(n.left, min, 5);
          R.d.delayTime.value = clamp(n.right, min, 5);
          const fb = n.freeze ? 1 : n.feedback;
          const feed = n.freeze ? 0 : 1;
          inLL.gain.value = (n.pingPong ? 0.5 : 1) * feed;
          inRL.gain.value = (n.pingPong ? 0.5 : 0) * feed;
          inRR.gain.value = (n.pingPong ? 0 : 1) * feed;
          fbLL.gain.value = n.pingPong ? 0 : fb;
          fbRR.gain.value = n.pingPong ? 0 : fb;
          fbLR.gain.value = n.pingPong ? fb : 0;
          fbRL.gain.value = n.pingPong ? fb : 0;
          for (const side of [L, R]) {
            side.hp.frequency.value = n.filter?.low ?? 10;
            side.lp.frequency.value = n.filter?.high ?? 22000;
          }
          m.set(n.mix);
        },
      };
    }
    case "chorus": {
      // Two modulated lines, the right's LFO inverted, for width.
      const m = mixer(ctx);
      const split = ctx.createChannelSplitter(2);
      const merge = ctx.createChannelMerger(2);
      const dl = ctx.createDelay(0.1);
      const dr = ctx.createDelay(0.1);
      const fbl = ctx.createGain();
      const fbr = ctx.createGain();
      const mod = lfo(ctx);
      const inv = ctx.createGain();
      inv.gain.value = -1;
      mod.out.connect(dl.delayTime);
      mod.out.connect(inv);
      inv.connect(dr.delayTime);
      const up = stereo(ctx);
      m.input.connect(up);
      up.connect(split);
      split.connect(dl, 0);
      split.connect(dr, 1);
      dl.connect(fbl);
      fbl.connect(dl);
      dr.connect(fbr);
      fbr.connect(dr);
      dl.connect(merge, 0, 0);
      dr.connect(merge, 0, 1);
      merge.connect(m.wet);
      return {
        input: m.input,
        output: m.output,
        nodes: [m.input, m.output, m.dry, m.wet, up, split, merge, dl, dr, fbl, fbr, mod.osc, mod.out, inv],
        sources: [mod.osc],
        accepts: (n) => n.kind === "chorus",
        apply: (n) => {
          if (n.kind !== "chorus") return;
          const base = Math.max(n.baseSec, n.depthSec + 128 / ctx.sampleRate);
          dl.delayTime.value = base;
          dr.delayTime.value = base;
          mod.osc.frequency.value = n.rateHz;
          mod.out.gain.value = n.depthSec;
          fbl.gain.value = n.feedback;
          fbr.gain.value = n.feedback;
          m.set(n.mix);
        },
      };
    }
    case "phaser": {
      const m = mixer(ctx);
      const poles = Array.from({ length: 6 }, () => {
        const f = ctx.createBiquadFilter();
        f.type = "allpass";
        f.Q.value = 0.7;
        return f;
      });
      const mod = lfo(ctx);
      const fbDelay = ctx.createDelay(0.01);
      const fb = ctx.createGain();
      let tail: AudioNode = m.input;
      const head = ctx.createGain();
      tail.connect(head);
      tail = head;
      for (const f of poles) {
        tail.connect(f);
        tail = f;
        mod.out.connect(f.frequency);
      }
      tail.connect(m.wet);
      tail.connect(fbDelay);
      fbDelay.connect(fb);
      fb.connect(head);
      return {
        input: m.input,
        output: m.output,
        nodes: [m.input, m.output, m.dry, m.wet, head, ...poles, mod.osc, mod.out, fbDelay, fb],
        sources: [mod.osc],
        accepts: (n) => n.kind === "phaser",
        apply: (n) => {
          if (n.kind !== "phaser") return;
          for (const f of poles) f.frequency.value = n.centerHz;
          mod.osc.frequency.value = n.rateHz;
          mod.out.gain.value = n.centerHz * 0.9 * n.depth;
          fbDelay.delayTime.value = 128 / ctx.sampleRate;
          fb.gain.value = n.feedback * 0.7;
          m.set(n.mix);
        },
      };
    }
    case "autopan": {
      const input = ctx.createGain();
      const panner = ctx.createStereoPanner();
      const trem = ctx.createGain();
      const mod = lfo(ctx, s.shape);
      const tremDepth = ctx.createGain();
      mod.osc.connect(tremDepth);
      input.connect(panner);
      panner.connect(trem);
      mod.out.connect(panner.pan);
      tremDepth.connect(trem.gain);
      const shape = s.shape;
      return {
        input,
        output: trem,
        nodes: [input, panner, trem, mod.osc, mod.out, tremDepth],
        sources: [mod.osc],
        accepts: (n) => n.kind === "autopan" && n.shape === shape,
        apply: (n) => {
          if (n.kind !== "autopan") return;
          mod.osc.frequency.value = n.rateHz;
          mod.out.gain.value = n.tremolo ? 0 : n.amount;
          tremDepth.gain.value = n.tremolo ? n.amount / 2 : 0;
          trem.gain.value = n.tremolo ? 1 - n.amount / 2 : 1;
        },
      };
    }
    case "utility": {
      // Width as an L/R matrix: L' = L·(1+w)/2 + R·(1-w)/2, and mirrored.
      const input = stereo(ctx);
      const split = ctx.createChannelSplitter(2);
      const merge = ctx.createChannelMerger(2);
      const ll = ctx.createGain();
      const lr = ctx.createGain();
      const rl = ctx.createGain();
      const rr = ctx.createGain();
      const pan = ctx.createStereoPanner();
      const gain = ctx.createGain();
      input.connect(split);
      split.connect(ll, 0);
      split.connect(lr, 0);
      split.connect(rl, 1);
      split.connect(rr, 1);
      ll.connect(merge, 0, 0);
      rl.connect(merge, 0, 0);
      lr.connect(merge, 0, 1);
      rr.connect(merge, 0, 1);
      merge.connect(pan);
      pan.connect(gain);
      return {
        input,
        output: gain,
        nodes: [input, split, merge, ll, lr, rl, rr, pan, gain],
        sources: [],
        accepts: (n) => n.kind === "utility",
        apply: (n) => {
          if (n.kind !== "utility") return;
          const sl = n.invertL ? -1 : 1;
          const sr = n.invertR ? -1 : 1;
          const same = (1 + n.width) / 2;
          const cross = (1 - n.width) / 2;
          ll.gain.value = sl * same;
          lr.gain.value = sl * cross;
          rl.gain.value = sr * cross;
          rr.gain.value = sr * same;
          pan.pan.value = n.pan;
          gain.gain.value = n.gain;
        },
      };
    }
  }
}

/** Build the stages as nodes on `ctx`; an empty chain is a straight wire. */
export function buildGraph(ctx: BaseAudioContext, stages: readonly Stage[]): DeviceGraph {
  const input = ctx.createGain();
  const built: Built[] = [];
  let tail: AudioNode = input;
  for (const s of stages) {
    const b = buildStage(ctx, s);
    tail.connect(b.input);
    tail = b.output;
    built.push(b);
  }
  built.forEach((b, i) => b.apply(stages[i]!));
  return {
    input,
    output: tail,
    update: (next) => {
      if (next.length !== built.length || next.some((s, i) => !built[i]!.accepts(s))) return false;
      built.forEach((b, i) => b.apply(next[i]!));
      return true;
    },
    dispose: () => {
      for (const b of built) {
        for (const src of b.sources) {
          try {
            src.stop();
          } catch {
            // never started, or already stopped
          }
        }
      }
      for (const n of [input, ...built.flatMap((b) => b.nodes)]) {
        try {
          n.disconnect();
        } catch {
          // already detached
        }
      }
    },
  };
}
